import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileImage,
  FileText,
  Languages,
  Loader2,
  ScanLine,
  Sparkles,
  UploadCloud,
  X,
} from 'lucide-react';
import menuImportsApi from '../../api/menuImportsApi';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getUserEstablishmentId } from '../Users/userManagementHelpers';
import '../../styles/menu-import/menu-import.css';
import '../../styles/menu-import/menu-import-upload.css';
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
];
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;
const LOCALES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr'];
const EXTRA_LANGUAGE_NAMES = {
  fr: 'Français',
  ar: 'العربية',
  en: 'English',
  es: 'Español',
  de: 'Deutsch',
  zh: '中文',
  ru: 'Русский',
  nl: 'Nederlands',
  ja: '日本語',
  it: 'Italiano',
  pt: 'Português',
  tr: 'Türkçe',
};
export default function MenuImport() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';
  const establishmentId = useMemo(
    () => getUserEstablishmentId(user),
    [user],
  );
  const [files, setFiles] = useState([]);
  const [targetLocales, setTargetLocales] = useState([
    'fr',
    'ar',
    'en',
  ]);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const showError = (message) => {
    showAlert({
      type: 'error',
      title: t('alerts.error'),
      message,
    });
  };
  const formatFileSize = (size) => {
    const megabytes = Number(size || 0) / 1024 / 1024;
    try {
      return `${new Intl.NumberFormat(language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(megabytes)} MB`;
    } catch {
      return `${megabytes.toFixed(2)} MB`;
    }
  };
  const addFiles = (incoming) => {
    const incomingFiles = Array.from(incoming || []);
    if (!incomingFiles.length) {
      return;
    }
    const invalidFile = incomingFiles.find(
      (file) =>
        !ACCEPTED_TYPES.includes(file.type) ||
        file.size > MAX_SIZE,
    );
    if (invalidFile) {
      showError(t('aiImport.upload.invalidFile'));
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }
    setFiles((currentFiles) => {
      const merged = [...currentFiles];
      incomingFiles.forEach((file) => {
        const duplicate = merged.some(
          (existing) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified,
        );
        if (!duplicate && merged.length < MAX_FILES) {
          merged.push(file);
        }
      });
      return merged;
    });
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };
  const removeFile = (fileToRemove) => {
    setFiles((current) =>
      current.filter((file) => file !== fileToRemove),
    );
  };
  const toggleLocale = (locale) => {
    setTargetLocales((current) =>
      current.includes(locale)
        ? current.filter((item) => item !== locale)
        : [...current, locale],
    );
  };
  const scan = async () => {
    if (!files.length) {
      showError(t('aiImport.upload.required'));
      return;
    }
    if (!targetLocales.length) {
      showError('Please select at least one language.');
      return;
    }
    try {
      setLoading(true);
      const response = await menuImportsApi.scan(
        establishmentId,
        files,
        targetLocales,
      );
      const menuImport = response.data?.data;
      if (!menuImport?.id) {
        throw new Error(t('aiImport.upload.invalidFile'));
      }
      navigate(`/menu-imports/${menuImport.id}/review`);
    } catch (requestError) {
      showError(getApiErrorMessage(requestError));
      setLoading(false);
    }
  };
  const renderSteps = () => (
    <div
      className="menu-import-steps menu-import-upload-steps"
      aria-label={t('aiImport.steps.label')}
    >
      <span className={!loading ? 'is-active' : 'is-complete'}>
        <b>1.</b>
        {t('aiImport.steps.upload')}
      </span>
      <span className={loading ? 'is-active' : ''}>
        <b>2.</b>
        {t('aiImport.steps.analysis')}
      </span>
      <span>
        <b>3.</b>
        {t('aiImport.steps.review')}
      </span>
      <span>
        <b>4.</b>
        {t('aiImport.steps.import')}
      </span>
    </div>
  );
  return (
    <main
      className="menu-import-page menu-import-upload-page"
      dir={direction}
    >
      <header className="menu-import-upload-header">
        <div className="menu-import-upload-header__copy">
          <span className="menu-import-page__eyebrow">
            <ScanLine size={16} />
            {t('aiImport.title')}
          </span>
          <h1>
            {loading
              ? t('aiImport.steps.analysis')
              : t('aiImport.upload.title')}
          </h1>
          <p>
            {loading
              ? t('aiImport.upload.analyzing')
              : t('aiImport.upload.description')}
          </p>
        </div>
        <span className="menu-import-upload-header__icon">
          {loading ? (
            <Sparkles size={28} />
          ) : (
            <UploadCloud size={28} />
          )}
        </span>
      </header>
      {renderSteps()}
      {loading ? (
        <section className="menu-import-upload-analysis">
          <div className="menu-import-upload-analysis__visual">
            <span className="menu-import-upload-analysis__orbit menu-import-upload-analysis__orbit--one" />
            <span className="menu-import-upload-analysis__orbit menu-import-upload-analysis__orbit--two" />
            <div className="menu-import-upload-analysis__icon">
              <Sparkles size={34} />
            </div>
          </div>
          <div className="menu-import-upload-analysis__content">
            <span>
              <ScanLine size={15} />
              {t('aiImport.steps.analysis')}
            </span>
            <h2>{t('aiImport.upload.analyzing')}</h2>
            <p>{t('aiImport.upload.description')}</p>
          </div>
          <div
            className="menu-import-upload-analysis__progress"
            aria-hidden="true"
          >
            <span />
          </div>
          <div className="menu-import-upload-analysis__files">
            {files.map((file) => {
              const Icon =
                file.type === 'application/pdf'
                  ? FileText
                  : FileImage;
              return (
                <div key={`${file.name}-${file.size}`}>
                  <span className="menu-import-upload-analysis__file-icon">
                    <Icon size={17} />
                  </span>
                  <span title={file.name}>{file.name}</span>
                  <Loader2
                    size={15}
                    className="is-spinning"
                  />
                </div>
              );
            })}
          </div>
        </section>
      ) : (
        <>
          <section className="menu-import-upload-card">
            <div className="menu-import-upload-card__head">
              <div>
                <span className="menu-import-upload-card__icon">
                  <UploadCloud size={21} />
                </span>
                <div>
                  <h2>{t('aiImport.upload.dropTitle')}</h2>
                  <p>{t('aiImport.upload.fileHint')}</p>
                </div>
              </div>
              <strong>
                {files.length}/{MAX_FILES}
              </strong>
            </div>
            <button
              type="button"
              className={`menu-import-upload-zone ${
                dragging ? 'is-dragging' : ''
              }`}
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setDragging(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                addFiles(event.dataTransfer.files);
              }}
            >
              <span className="menu-import-upload-zone__icon">
                <UploadCloud size={34} />
              </span>
              <strong>{t('aiImport.upload.dropTitle')}</strong>
              <span>{t('aiImport.upload.dropDescription')}</span>
              <small>{t('aiImport.upload.fileHint')}</small>
            </button>
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(event) => addFiles(event.target.files)}
            />
            {files.length > 0 ? (
              <div className="menu-import-upload-files">
                {files.map((file) => {
                  const Icon =
                    file.type === 'application/pdf'
                      ? FileText
                      : FileImage;
                  return (
                    <article
                      className="menu-import-upload-file"
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                    >
                      <span className="menu-import-upload-file__icon">
                        <Icon size={20} />
                      </span>
                      <div className="menu-import-upload-file__copy">
                        <strong title={file.name}>{file.name}</strong>
                        <span>{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="menu-import-upload-file__remove"
                        onClick={() => removeFile(file)}
                        aria-label={t('common.delete')}
                      >
                        <X size={17} />
                      </button>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </section>
          <section className="menu-import-upload-card">
            <div className="menu-import-upload-card__head">
              <div>
                <span className="menu-import-upload-card__icon">
                  <Languages size={21} />
                </span>
                <div>
                  <h2>{t('aiImport.languages.title')}</h2>
                  <p>{t('aiImport.languages.description')}</p>
                </div>
              </div>
              <strong>{targetLocales.length} / {LOCALES.length}</strong>
            </div>
            <div className="menu-import-upload-language-grid">
              {LOCALES.map((locale) => {
                const selected = targetLocales.includes(locale);
                return (
                  <label
                    className={`menu-import-upload-language ${
                      selected ? 'is-selected' : ''
                    }`}
                    key={locale}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleLocale(locale)}
                    />
                    <span className="menu-import-upload-language__check">
                      {selected ? '✓' : ''}
                    </span>
                    <span>
                      {EXTRA_LANGUAGE_NAMES[locale] || t(`aiImport.languages.${locale}`)}
                    </span>
                    <small>{locale.toUpperCase()}</small>
                  </label>
                );
              })}
            </div>
          </section>
          <div className="menu-import-upload-actions">
            <button
              type="button"
              className="menu-import-upload-scan-button"
              onClick={scan}
              disabled={!files.length || loading}
            >
              <ScanLine size={19} />
              <span>{t('aiImport.upload.scanButton')}</span>
            </button>
          </div>
        </>
      )}
    </main>
  );
}
